<template>
  <form id="edit-role" class="space-y-5" @submit="onSubmit">
    <fieldset class="flex flex-col gap-1">
      <Label for="name">Name</Label>
      <Input
        v-bind="nameAttrs"
        id="name"
        v-model="name"
        :disabled="
          !supportedFeatures['custom_roles'].is_supported ||
          !role.is_deletable ||
          !hasManageRolePermissions
        "
      />
      <span class="text-error-400">{{ errors?.name }}</span>
    </fieldset>
    <fieldset class="flex flex-col gap-1">
      <Label for="description">Description</Label>
      <Textarea
        v-bind="descriptionAttrs"
        id="description"
        v-model="description"
        :disabled="
          !supportedFeatures['custom_roles'].is_supported ||
          !role.is_deletable ||
          !hasManageRolePermissions
        "
      />
      <span class="text-error-400">{{ errors?.description }}</span>
    </fieldset>
    <div class="divide-y divide-primary-500">
      <div
        v-for="p in permissions"
        :key="p.key"
        class="flex justify-between py-5"
      >
        <div class="space-y-2.5">
          <Label :for="p.key" class="text-base font-semibold text-white">
            {{ p.name }}
          </Label>
          <div class="text-sm text-gray-300">{{ p.description }}</div>
        </div>
        <div>
          <SwitchRoot
            :id="p.key"
            :disabled="
              !supportedFeatures['custom_roles'].is_supported ||
              !role.is_deletable ||
              !hasManageRolePermissions ||
              disabledPermissions.includes(p.key)
            "
            class="w-[48px] h-[22px] focus-within:outline focus-within:outline-[#1D2121] flex bg-[#1D2121] shadow-sm rounded-full relative data-[state=checked]:bg-accent-400 cursor-default disabled:bg-[#FAFAFA1F] disabled:data-[state=checked]:bg-accent-400/40"
            :default-checked="role.permissions_keys.includes(p.key)"
            :checked="formValues.selectedPermissions.includes(p.key)"
            @update:checked="changePermissions($event, p.key)"
          >
            <SwitchThumb
              class="block w-[18px] h-[18px] my-auto bg-neutral-300 data-[disabled]:bg-[#FAFAFA61] shadow-sm rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=unchecked]:translate-x-[3px] data-[state=checked]:translate-x-[26px]"
            />
          </SwitchRoot>
        </div>
      </div>
    </div>

    <StickyBottomCTA
      :is-pending="isPending"
      :meta="meta"
      :reset-form="resetForm"
    />
  </form>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { toast } from 'vue-sonner';

import { SwitchRoot, SwitchThumb } from 'radix-vue';

import requests from '@vuex/requests.js';
import { SYS_PERMISSIONS } from '@/constants.js';

import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';
import { Textarea } from '@ui/components/textarea';
import { useProtect } from '@composables/useProtect.js';
import StickyBottomCTA from '@components/common/StickyBottomCTA.vue';
import { queries } from '@/queries/index.js';

const store = useStore();
const queryClient = useQueryClient();
const validationSchema = toTypedSchema(
  z.object({
    name: z
      .string()
      .min(1, {
        message: 'The name field is required',
      })
      .max(255),
    description: z
      .string()
      .min(1, {
        message: 'The description field is required',
      })
      .max(255),
    selectedPermissions: z.array(z.string()),
  })
);
const props = defineProps({
  role: {
    type: Object,
    required: true,
  },
  permissions: {
    type: Array,
    required: true,
  },
});

const supportedFeatures = computed(() => store.getters['features/getFeatures']);

const { hasPermission: hasManageRolePermissions } = useProtect({
  permissions: [SYS_PERMISSIONS.SYS_ROLES_MANAGE],
});

const {
  handleSubmit,
  errors,
  defineField,
  resetForm,
  meta,
  setValues,
  values: formValues,
} = useForm({
  validationSchema,
  initialValues: {
    name: props.role.name,
    description: props.role.description,
    selectedPermissions: props.role.permissions_keys,
  },
});

watch(
  () => props.role?.permissions_keys,
  () => {
    resetForm({
      values: {
        name: props.role.name,
        description: props.role.description,
        selectedPermissions: props.role.permissions_keys,
      },
    });
  }
);

const [name, nameAttrs] = defineField('name');
const [description, descriptionAttrs] = defineField('description');

/**
 * The permissions that a permission requires
 */
const permissionRequires = computed(() => {
  return props.permissions?.reduce((acc, p) => {
    if (p.dependent_keys) {
      acc[p.key] = p.dependent_keys;
    }
    return acc;
  }, {});
});

/**
 * An object containing the permissions that are required by a permission
 */
const permissionRequiredBy = computed(() => {
  return props.permissions?.reduce((acc, p) => {
    if (p.dependent_keys) {
      p.dependent_keys.forEach(r => {
        if (!acc[r]) {
          acc[r] = [];
        }
        acc[r].push(p.key);
      });
    }
    return acc;
  }, {});
});

/**
 * The permissions that are disabled based on the selected permissions
 */
const disabledPermissions = computed(() => {
  return formValues.selectedPermissions.filter(p => {
    const requiredBy = permissionRequiredBy.value?.[p] || [];
    return requiredBy.some(r => formValues.selectedPermissions.includes(r));
  });
});

const changePermissions = async (value, key) => {
  const requiredPermissions = permissionRequires.value?.[key] || [];
  const requiredBy = !value ? permissionRequiredBy.value?.[key] || [] : [];
  setValues({
    selectedPermissions: value
      ? [
          ...new Set([
            ...formValues.selectedPermissions,
            key,
            ...requiredPermissions,
          ]),
        ]
      : formValues.selectedPermissions.filter(
          p => ![key, ...requiredBy].includes(p)
        ),
  });
};

const { isPending, mutate } = useMutation({
  mutationFn: async payload => {
    await requests.roles.update(store.getters['workspace/getId'], {
      id: props.role.id,
      payload,
    });
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({
      queryKey: queries.workspace.roles.queryKey,
    });
    await queryClient.invalidateQueries({
      queryKey: ['role', props.role.id],
    });
    toast.success('The role has been updated!');
  },
  onError: () => {
    console.log(formValues);
    resetForm({
      values: {
        name: props.role.name,
        description: props.role.description,
        selectedPermissions: props.role.permissions_keys,
      },
    });
    toast.error('Something went wrong, please try again later');
  },
});

const onSubmit = handleSubmit((values, { resetForm: reset }) => {
  mutate(values);
  reset({
    values,
  });
});
</script>
